import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  CurrencyRupeeIcon, ShoppingBagIcon, UsersIcon, TicketIcon,
  CubeIcon, ClockIcon
} from "@heroicons/react/24/outline";
import { adminAPI } from "../../services/api";
import AdminLayout from "../../components/admin/AdminLayout";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { format } from "date-fns";

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  paid: "bg-blue-100 text-blue-800",
  processing: "bg-purple-100 text-purple-800",
  shipped: "bg-indigo-100 text-indigo-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.dashboard().then((res) => setStats(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <AdminLayout><LoadingSpinner className="py-20" size="lg" /></AdminLayout>;

  const cards = [
    { label: "Total Revenue", value: `₹${(stats?.total_revenue || 0).toLocaleString("en-IN")}`, icon: CurrencyRupeeIcon, color: "text-green-600 bg-green-50" },
    { label: "Monthly Revenue", value: `₹${(stats?.monthly_revenue || 0).toLocaleString("en-IN")}`, icon: CurrencyRupeeIcon, color: "text-blue-600 bg-blue-50" },
    { label: "Orders Today", value: stats?.orders_today || 0, icon: ShoppingBagIcon, color: "text-primary-600 bg-primary-50" },
    { label: "Total Customers", value: stats?.total_customers || 0, icon: UsersIcon, color: "text-purple-600 bg-purple-50" },
    { label: "Pending Orders", value: stats?.pending_orders || 0, icon: ClockIcon, color: "text-orange-600 bg-orange-50" },
    { label: "Open Tickets", value: stats?.open_tickets || 0, icon: TicketIcon, color: "text-yellow-600 bg-yellow-50" },
  ];

  return (
    <AdminLayout>
      <Helmet><title>Dashboard – Admin | Kushi Fashions</title></Helmet>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {cards.map((card) => (
          <div key={card.label} className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500">{card.label}</p>
              <div className={`p-2 rounded-xl ${card.color}`}>
                <card.icon className="h-5 w-5" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{card.value}</p>
          </div>
        ))}
      </div>

      {stats?.low_stock_count > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <CubeIcon className="h-5 w-5 text-orange-500 flex-shrink-0" />
          <p className="text-sm text-orange-700">
            <strong>{stats.low_stock_count} products</strong> are low on stock (less than 5 items). Consider restocking.
          </p>
        </div>
      )}

      {/* Recent orders */}
      <div className="card p-5">
        <h2 className="font-bold text-gray-900 mb-4">Recent Orders</h2>
        {stats?.recent_orders?.length === 0 ? (
          <p className="text-gray-400 text-sm">No orders yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-3 pr-4">Order #</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3 pr-4">Amount</th>
                  <th className="pb-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {stats?.recent_orders?.map((order: any) => (
                  <tr key={order.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-3 pr-4 font-medium text-gray-800">{order.order_number}</td>
                    <td className="py-3 pr-4">
                      <span className={`badge ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-600"}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-primary-600 font-medium">
                      ₹{Number(order.total_amount).toLocaleString("en-IN")}
                    </td>
                    <td className="py-3 text-gray-400">
                      {format(new Date(order.created_at), "dd MMM, h:mm a")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
